import { cloneElement, useId, useLayoutEffect, useRef, useState, useSyncExternalStore, type ComponentProps, type ReactElement, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { DayButton } from '@daypicker/react';
import { Calendar } from '@unitfield/ui/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@unitfield/ui/components/ui/popover';
import { UnitfieldIcon } from '@unitfield/ui/icons';
import { formatLongDate, parseIsoDate } from '../rental-applications/listing-model';
import { usePublicShell } from './public-brand-provider';
import { dateSelectionValue } from './date-picker-model';
import styles from './shell.module.css';
import calendar from './calendar.module.css';

/** A day cell on the shell's tokens: wells for hover, the brand only for the chosen day. */
function PublicDayButton({ modifiers, ...props }: ComponentProps<typeof DayButton>) {
  return (
    <DayButton
      {...props}
      modifiers={modifiers}
      className={calendar.day}
      data-selected={modifiers.selected || undefined}
      data-today={modifiers.today || undefined}
      data-outside={modifiers.outside || undefined}
    />
  );
}

export type CalendarRange = 'future' | 'past';

/**
 * The kit calendar on the public tokens. `future` pages month by month from `min`;
 * `past` (birthdays, jobs, old addresses) adds month and year menus.
 */
export function DateCalendar({
  value,
  min,
  max,
  range = 'future',
  label,
  onSelect,
}: {
  value: string | null;
  min?: string;
  max?: string;
  range?: CalendarRange;
  label?: string;
  onSelect: (value: string) => void;
}) {
  const selected = parseIsoDate(value) ?? undefined;
  const minDate = parseIsoDate(min) ?? undefined;
  const maxDate = parseIsoDate(max) ?? undefined;
  const now = new Date();
  const startMonth = minDate ?? (range === 'past' ? new Date(now.getFullYear() - 120, 0, 1) : undefined);
  const endMonth = maxDate ?? (range === 'past' ? new Date(now.getFullYear() + 5, 11, 1) : undefined);
  const preferred = selected ?? now;
  const initialMonth = minDate && preferred < minDate ? minDate : maxDate && preferred > maxDate ? maxDate : preferred;
  const disabled = [minDate ? { before: minDate } : null, maxDate ? { after: maxDate } : null].filter((matcher) => matcher !== null);
  return (
    <Calendar
      mode="single"
      required
      autoFocus
      aria-label={label}
      selected={selected}
      defaultMonth={initialMonth}
      startMonth={startMonth}
      endMonth={endMonth}
      disabled={disabled}
      captionLayout={range === 'past' ? 'dropdown' : 'label'}
      onSelect={(date) => {
        if (!date) return;
        const next = dateSelectionValue(date, min, max);
        if (next !== null) onSelect(next);
      }}
      className={calendar.calendar}
      classNames={{
        root: calendar.root,
        months: calendar.months,
        month: calendar.month,
        nav: calendar.nav,
        button_previous: calendar.navButton,
        button_next: calendar.navButton,
        month_caption: calendar.caption,
        caption_label: calendar.captionLabel,
        dropdowns: calendar.dropdowns,
        dropdown_root: calendar.dropdownRoot,
        dropdown: calendar.dropdown,
        month_grid: calendar.grid,
        weekdays: calendar.weekdays,
        weekday: calendar.weekday,
        week: calendar.week,
        day: calendar.cell,
        day_button: calendar.day,
        selected: calendar.selected,
        today: calendar.today,
        outside: calendar.outside,
        disabled: calendar.disabled,
        hidden: calendar.hidden,
      }}
      components={{ DayButton: PublicDayButton }}
    />
  );
}

/** The calendar's desktop card, portalled into the shell. */
export function DatePopoverContent({ children, label }: { children: ReactNode; label: string }) {
  const { container } = usePublicShell();
  // Never fall back to document.body: that would inherit the dashboard theme.
  if (!container) return null;
  return (
    <PopoverContent container={container} align="start" sideOffset={8} collisionPadding={12} className={calendar.popover} aria-label={label}>
      <div className={calendar.card}>{children}</div>
    </PopoverContent>
  );
}

const phoneQuery = '(max-width: 640px)';
const phoneSnapshot = () => window.matchMedia?.(phoneQuery).matches ?? false;
const desktopSnapshot = () => false;
function subscribePhone(onChange: () => void) {
  const query = window.matchMedia?.(phoneQuery);
  query?.addEventListener('change', onChange);
  return () => query?.removeEventListener('change', onChange);
}

function Sheet({ id, label, children, onClose, returnFocus }: {
  id: string;
  label: string;
  children: ReactNode;
  onClose: () => void;
  returnFocus: RefObject<HTMLButtonElement | null>;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  // The top layer escapes the shell's container-query containing block without
  // moving its content out of the theme. The browser owns modal focus and Escape.
  useLayoutEffect(() => {
    const dialog = ref.current!;
    dialog.showModal();
    return () => {
      dialog.close();
      returnFocus.current?.focus({ preventScroll: true });
    };
  }, [returnFocus]);

  return (
    <dialog
      ref={ref}
      id={id}
      className={calendar.sheet}
      aria-label={label}
      aria-modal="true"
      onCancel={(event) => { event.preventDefault(); onClose(); }}
      // A `close` event arrives a task after `dialog.close()`. When the dialog was opened again in between (React's development double effect), that event is
      // stale and must not close the sheet the applicant just opened.
      onClose={(event) => { if (!event.currentTarget.open) onClose(); }}
      onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}
    >
      <div className={calendar.sheetBody}>
        <div className={calendar.grabber} aria-hidden="true" />
        {children}
      </div>
    </dialog>
  );
}

/**
 * A popover on desktop; on phones, a modal bottom sheet in the same public shell. The calendar card
 * is the default desktop surface; the listing's price token brings its own.
 */
export function SheetPopover({ open, onOpenChange, trigger, label, children, popover }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: ReactElement<ComponentProps<'button'>>;
  label: string;
  children: ReactNode;
  /** The desktop surface around `children`; a `PopoverContent`. */
  popover?: (children: ReactNode) => ReactNode;
}) {
  const phone = useSyncExternalStore(subscribePhone, phoneSnapshot, desktopSnapshot);
  const { container } = usePublicShell();
  const id = useId();
  const returnFocus = useRef<HTMLButtonElement | null>(null);
  if (phone) {
    return <>
      {cloneElement(trigger, {
        ref: returnFocus,
        'aria-haspopup': 'dialog',
        'aria-expanded': open,
        'aria-controls': open ? id : undefined,
        onClick: () => onOpenChange(true),
      })}
      {open && container ? createPortal(
        <Sheet id={id} label={label} returnFocus={returnFocus} onClose={() => onOpenChange(false)}>{children}</Sheet>,
        container,
      ) : null}
    </>;
  }
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      {popover ? popover(children) : <DatePopoverContent label={label}>{children}</DatePopoverContent>}
    </Popover>
  );
}

/**
 * A filled date well that reads "Nov 1, 2026" and opens the shell's responsive calendar,
 * portalled into the `.public-shell` root so the business's theme and brand apply there.
 */
export function DateWell({
  id,
  value,
  min,
  placeholder = 'Pick a date',
  label,
  className,
  onChange,
  max,
  range = 'future',
  invalid,
  describedBy,
  disabled,
  clearable = true,
  onBlur,
}: {
  id?: string;
  value: string | null;
  min?: string;
  max?: string;
  placeholder?: string;
  /** Accessible name when no visible <label for> points here. */
  label?: string;
  className?: string;
  range?: CalendarRange;
  invalid?: boolean;
  describedBy?: string;
  disabled?: boolean;
  clearable?: boolean;
  onBlur?: () => void;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const valueId = useId();
  const date = parseIsoDate(value);
  const text = date ? formatLongDate(date) : placeholder;
  const select = (next: string) => {
    onChange(next);
    setOpen(false);
    onBlur?.();
  };
  return (
    <SheetPopover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) onBlur?.();
      }}
      label={label ? `Choose ${label.toLowerCase()}` : 'Choose a date'}
      trigger={
        <button
          id={id}
          type="button"
          className={`${styles.dateWell} ${className ?? ''}`}
          data-invalid={invalid ? '' : undefined}
          data-open={open ? '' : undefined}
          disabled={disabled}
          aria-haspopup="dialog"
          aria-label={label ? `${label}: ${text}` : undefined}
          aria-describedby={[label ? null : valueId, describedBy].filter(Boolean).join(' ') || undefined}
          aria-invalid={invalid || undefined}
        >
          <UnitfieldIcon name="calendar" size={18} aria-hidden="true" />
          <span id={valueId} className={styles.dateText} data-empty={date ? undefined : ''}>{text}</span>
        </button>
      }
    >
      <DateCalendar value={value} min={min} max={max} range={range} label={label} onSelect={select} />
      {date && clearable ? <button type="button" className={calendar.clear} onClick={() => select('')}>Clear date</button> : null}
    </SheetPopover>
  );
}
